import React, { useState } from 'react';
import { api } from '../../utils/api';

interface ForcePasswordResetPageProps {
    resetToken: string;
    onSuccess: () => void;
}

const ForcePasswordResetPage: React.FC<ForcePasswordResetPageProps> = ({ resetToken, onSuccess }) => {
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }
        setError(null);
        setLoading(true);

        try {
            // This endpoint is special and uses the resetToken for authorization, not the standard token
            // So we use a direct fetch but still could be wrapped in api utility if needed.
            // For now, this is a specific case.
            const response = await fetch('/api/v1/auth/force-reset-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${resetToken}`
                },
                body: JSON.stringify({ newPassword })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || "Failed to update password.");
            onSuccess();
        } catch (err) {
            if(err instanceof Error) setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Update Your Password</h2>
                <p className="text-gray-600 mb-6">For security, you must change your temporary password before you can continue.</p>
                <form onSubmit={handleSubmit}>
                    {error && <p className="text-red-500 text-center mb-4">{error}</p>}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700" htmlFor="new-password">New Password</label>
                        <input
                            id="new-password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                            required
                        />
                    </div>
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700" htmlFor="confirm-new-password">Confirm New Password</label>
                        <input
                            id="confirm-new-password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                            required
                        />
                    </div>
                    <button type="submit" className="w-full py-2 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700" disabled={loading}>
                        {loading ? 'Updating...' : 'Set New Password and Continue'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ForcePasswordResetPage;